/**
 * B5 (V1): Materials CSV import.
 *
 * Contract (no new schema, no new semantics):
 * - Maps only fields the existing Add Material workflow writes
 *   (buildMaterialCatalogWrite): material_name (required), sku, description,
 *   unit_of_measure, material_type (Product / Service), unit_cost,
 *   current_inventory. Validation is that same function's validation.
 * - Authorization is the existing boundary: material.catalog.edit in the UI and
 *   the materials RLS policy at the database (insert = org owner/admin in their
 *   own organization). Rows are stamped with the actor's organization; there is
 *   no service-role path.
 * - Duplicates are allowed by the schema (no unique name/SKU), so identity is
 *   ambiguous. Safe rule: a row is a POSSIBLE DUPLICATE when its SKU matches
 *   (case/whitespace-insensitive), or its name + unit match, an existing
 *   same-organization material (active or archived) or an earlier row in the
 *   file. Possible duplicates are skipped by default; nothing existing is ever
 *   updated.
 * - Idempotency: every prepared row gets a client-generated material_id once,
 *   at preview time. Inserts use ON CONFLICT (material_id) DO NOTHING, and a row
 *   counts as imported only after it is read back from the database. Retrying
 *   the same prepared import can therefore never create a second copy.
 * - Online only: never queued offline. A lost connection stops the import and
 *   reports exactly which rows were confirmed.
 */
import { supabase } from "../supabase";
import { can, CAPABILITY } from "./permissions";
import { isOffline } from "./connectivity";
import { buildMaterialCatalogWrite } from "./materialsCatalog";
import { resolveOrganizationIdForQueries } from "./orgQueryScope";
import { csvCell, CSV_BOM } from "./accountantCsvExport";

export const IMPORT_MAX_ROWS = 5000;
export const IMPORT_MAX_BYTES = 2 * 1024 * 1024;
export const IMPORT_CHUNK_SIZE = 100;

export const IMPORT_COLUMNS = Object.freeze([
  { field: "material_name", header: "Name", required: true, aliases: ["name", "material name", "material_name", "material"] },
  { field: "sku", header: "SKU", aliases: ["sku", "item code", "code", "part number"] },
  { field: "description", header: "Description", aliases: ["description", "desc"] },
  { field: "unit_of_measure", header: "Unit", aliases: ["unit", "unit of measure", "unit_of_measure", "uom"] },
  { field: "material_type", header: "Type", aliases: ["type", "material type", "material_type"] },
  { field: "unit_cost", header: "Unit Cost", aliases: ["unit cost", "unit_cost", "cost", "unit rate", "rate"] },
  { field: "current_inventory", header: "Inventory", aliases: ["inventory", "current inventory", "current_inventory", "quantity on hand", "on hand"] },
]);

export const ROW_STATUS = Object.freeze({ VALID: "valid", INVALID: "invalid", DUPLICATE: "duplicate" });

/** RFC 4180 parser. Handles BOM, quotes, "" escapes, embedded commas/newlines, CRLF/LF/CR. */
export function parseCsv(text, delimiter = null) {
  let src = String(text ?? "");
  if (src.charCodeAt(0) === 0xfeff) src = src.slice(1);
  const firstLine = src.split(/\r\n|\n|\r/, 1)[0] || "";
  const delim = delimiter || (!firstLine.includes(",") && firstLine.includes(";") ? ";" : ",");
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i += 2; continue; }
        quoted = false; i += 1; continue;
      }
      cell += ch; i += 1; continue;
    }
    if (ch === '"' && cell === "") { quoted = true; i += 1; continue; }
    if (ch === delim) { row.push(cell); cell = ""; i += 1; continue; }
    if (ch === "\r" || ch === "\n") {
      row.push(cell); rows.push(row); row = []; cell = "";
      i += ch === "\r" && src[i + 1] === "\n" ? 2 : 1;
      continue;
    }
    cell += ch; i += 1;
  }
  if (quoted) return { rows: [], delimiter: delim, error: "The file has an unclosed quote. Check the CSV and try again." };
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return { rows, delimiter: delim, error: null };
}

export function normalizeHeader(value) {
  return String(value ?? "")
    .replace(/\((required|optional)\)/gi, "")
    .replace(/\*/g, "")
    .replace(/[_\s]+/g, " ")
    .trim()
    .toLowerCase();
}

const norm = (v) => String(v ?? "").replace(/\s+/g, " ").trim().toLowerCase();
export const nameUnitKey = (name, unit) => `${norm(name)}|${norm(unit)}`;
export const skuKey = (sku) => norm(sku);

function resolveType(value) {
  const t = norm(value);
  if (!t || t === "product" || t === "material") return { value: "Product" };
  if (t === "service") return { value: "Service" };
  return { error: "Type must be Material/Product or Service." };
}

function newId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  // RFC 4122 v4 fallback for older WebViews.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Parse + validate + duplicate-check. Pure apart from id generation.
 * @param {string} text  file contents
 * @param {{ existing?: Array<{material_name, sku, unit_of_measure, active}> }} options
 */
export function prepareMaterialsImport(text, { existing = [] } = {}) {
  const parsed = parseCsv(text);
  if (parsed.error) return { error: parsed.error, rows: [] };
  const rows = parsed.rows;
  if (!rows.length) return { error: "The file is empty.", rows: [] };

  const header = rows[0].map(normalizeHeader);
  const map = {};
  const unknown = [];
  header.forEach((h, idx) => {
    if (!h) return;
    const col = IMPORT_COLUMNS.find((c) => c.aliases.includes(h));
    if (col && map[col.field] === undefined) map[col.field] = idx;
    else if (!col) unknown.push(rows[0][idx].trim());
  });
  if (map.material_name === undefined) {
    return { error: "A \"Name\" column is required. Download the template to see the supported columns.", rows: [] };
  }

  const existingSku = new Map();
  const existingNameUnit = new Map();
  for (const m of existing || []) {
    if (skuKey(m.sku)) existingSku.set(skuKey(m.sku), m);
    existingNameUnit.set(nameUnitKey(m.material_name, m.unit_of_measure), m);
  }
  const fileSku = new Map();
  const fileNameUnit = new Map();
  const out = [];

  for (let r = 1; r < rows.length; r += 1) {
    const cells = rows[r];
    if (cells.every((c) => String(c).trim() === "")) continue; // blank row
    const get = (f) => (map[f] === undefined ? "" : String(cells[map[f]] ?? "").trim());
    const line = r + 1;
    const form = {
      material_name: get("material_name"),
      sku: get("sku"),
      description: get("description"),
      unit_of_measure: get("unit_of_measure"),
      unit_cost: get("unit_cost"),
      current_inventory: get("current_inventory"),
    };
    const reasons = [];
    const type = resolveType(get("material_type"));
    if (type.error) reasons.push(type.error);
    const built = buildMaterialCatalogWrite({ ...form, material_type: type.value || "" });
    if (built.error) reasons.push(built.error);
    if (cells.length > header.length && cells.slice(header.length).some((c) => String(c).trim())) {
      reasons.push("Row has more values than the header has columns.");
    }

    const entry = { line, form, payload: null, status: ROW_STATUS.VALID, reasons, duplicateOf: null, id: null };
    if (reasons.length) {
      entry.status = ROW_STATUS.INVALID;
      out.push(entry);
      continue;
    }
    entry.payload = built.value;

    const sk = skuKey(form.sku);
    const nu = nameUnitKey(form.material_name, form.unit_of_measure);
    const hitExisting = (sk && existingSku.get(sk)) || existingNameUnit.get(nu);
    const hitFile = (sk && fileSku.get(sk)) || fileNameUnit.get(nu);
    if (hitExisting) {
      entry.status = ROW_STATUS.DUPLICATE;
      entry.duplicateOf = { kind: "catalog", name: hitExisting.material_name, sku: hitExisting.sku || "", archived: hitExisting.active === false };
      entry.reasons = [`Matches existing material "${hitExisting.material_name}"${hitExisting.active === false ? " (archived)" : ""}${sk && skuKey(hitExisting.sku) === sk ? " by SKU" : " by name and unit"}.`];
    } else if (hitFile) {
      entry.status = ROW_STATUS.DUPLICATE;
      entry.duplicateOf = { kind: "file", line: hitFile.line };
      entry.reasons = [`Same ${sk && fileSku.get(sk) ? "SKU" : "name and unit"} as row ${hitFile.line} in this file.`];
    }
    if (sk && !fileSku.has(sk)) fileSku.set(sk, entry);
    if (!fileNameUnit.has(nu)) fileNameUnit.set(nu, entry);
    entry.id = newId();
    out.push(entry);
  }

  const dataRows = out.length;
  if (!dataRows) return { error: "The file has a header but no material rows.", rows: [] };
  if (dataRows > IMPORT_MAX_ROWS) return { error: `The file has ${dataRows} rows. Import up to ${IMPORT_MAX_ROWS} at a time.`, rows: [] };

  return {
    error: null,
    rows: out,
    unknownColumns: unknown,
    delimiter: parsed.delimiter,
    counts: {
      valid: out.filter((x) => x.status === ROW_STATUS.VALID).length,
      invalid: out.filter((x) => x.status === ROW_STATUS.INVALID).length,
      duplicate: out.filter((x) => x.status === ROW_STATUS.DUPLICATE).length,
    },
  };
}

/** Template: supported headers (required marked) and one example row. */
export function buildMaterialsImportTemplate() {
  const header = IMPORT_COLUMNS.map((c) => (c.required ? `${c.header} (required)` : c.header));
  const example = ["Ceramic Tile 12x12", "TILE-1212", "Glazed floor tile", "sq ft", "Material", "3.25", "500"];
  const example2 = ["Tile installation", "", "Labour rate", "hour", "Service", "65", ""];
  return `${CSV_BOM}${[header, example, example2].map((r) => r.map(csvCell).join(",")).join("\r\n")}\r\n`;
}

export function isNetworkError(error) {
  const text = `${error?.name || ""} ${error?.message || ""}`;
  return /Failed to fetch|NetworkError|Network request failed|Load failed|fetch failed|TypeError: Failed|ERR_INTERNET|timeout/i.test(text);
}

/**
 * Persist rows the user confirmed. Never counts a row without read-back.
 * @returns {{ imported: object[], failed: {entry, reason}[], notAttempted: object[], stoppedReason: string|null }}
 */
export async function commitMaterialsImport(entries, { actor, client = supabase, chunkSize = IMPORT_CHUNK_SIZE, onProgress } = {}) {
  if (!can(actor, CAPABILITY.MATERIAL_CATALOG_EDIT)) {
    const e = new Error("You do not have permission to import materials.");
    e.code = "CP_IMPORT_FORBIDDEN";
    throw e;
  }
  if (isOffline()) {
    const e = new Error("You're offline. Importing materials needs a connection; nothing was saved.");
    e.code = "CP_IMPORT_OFFLINE";
    throw e;
  }
  const organizationId = await resolveOrganizationIdForQueries(actor, client);
  if (!organizationId) throw new Error("Your organization could not be resolved, so nothing was imported.");

  const todo = (entries || []).filter((e) => e && e.id && e.payload);
  const result = { imported: [], failed: [], notAttempted: [], stoppedReason: null };

  for (let start = 0; start < todo.length; start += chunkSize) {
    const chunk = todo.slice(start, start + chunkSize);
    if (isOffline()) {
      result.stoppedReason = "Connection lost. The remaining rows were not sent.";
      result.notAttempted.push(...todo.slice(start));
      break;
    }
    const payload = chunk.map((e) => ({ ...e.payload, material_id: e.id, organization_id: organizationId }));
    let writeError = null;
    try {
      const { error } = await client
        .from("materials")
        .upsert(payload, { onConflict: "material_id", ignoreDuplicates: true });
      writeError = error || null;
    } catch (error) {
      writeError = error;
    }

    // Read back what actually exists for this chunk, whatever the write said.
    let confirmed = null;
    try {
      const { data, error } = await client
        .from("materials")
        .select("material_id")
        .eq("organization_id", organizationId)
        .in("material_id", chunk.map((e) => e.id));
      if (!error) confirmed = new Set((data || []).map((r) => r.material_id));
    } catch (_) {
      confirmed = null;
    }

    if (confirmed === null) {
      // Could not verify: report honestly, stop, and let a retry (same ids) settle it.
      for (const e of chunk) result.failed.push({ entry: e, reason: "Not confirmed (connection problem). Retrying is safe and will not create a copy." });
      result.notAttempted.push(...todo.slice(start + chunkSize));
      result.stoppedReason = "The connection failed during import. Rows not listed as imported were not confirmed.";
      break;
    }
    for (const e of chunk) {
      if (confirmed.has(e.id)) result.imported.push(e);
      else result.failed.push({ entry: e, reason: writeError?.message || "The database did not save this row." });
    }
    onProgress?.({ done: Math.min(start + chunkSize, todo.length), total: todo.length });
    if (writeError && isNetworkError(writeError)) {
      result.notAttempted.push(...todo.slice(start + chunkSize));
      result.stoppedReason = "The connection failed during import. Rows not listed as imported were not confirmed.";
      break;
    }
  }
  return result;
}

/**
 * Concurrency mitigation (no schema change): re-run the catalog duplicate rule
 * against a catalog re-read immediately before submit. Rows that were valid at
 * preview but now match a material someone else created meanwhile are skipped
 * as possible duplicates. Rows the user explicitly chose to import as new stay.
 * Limits: a material created between this re-check and the insert, or by a
 * second import running at the same time, is not detected (no uniqueness
 * constraint exists). Fixed row ids only make retries of THIS prepared import
 * safe; they do not stop two different imports of equivalent materials.
 */
export function recheckDuplicatesBeforeSubmit(entries, existing = []) {
  const bySku = new Map();
  const byNameUnit = new Map();
  for (const m of existing || []) {
    if (skuKey(m.sku)) bySku.set(skuKey(m.sku), m);
    byNameUnit.set(nameUnitKey(m.material_name, m.unit_of_measure), m);
  }
  const send = [];
  const skipped = [];
  for (const e of entries || []) {
    if (e?.status !== ROW_STATUS.VALID) { send.push(e); continue; }
    const sk = skuKey(e.form?.sku);
    const hit = (sk && bySku.get(sk)) || byNameUnit.get(nameUnitKey(e.form?.material_name, e.form?.unit_of_measure));
    if (hit && hit.material_id !== e.id) {
      skipped.push({ ...e, status: ROW_STATUS.DUPLICATE, reasons: [`Possible duplicate: "${hit.material_name}" was added to the catalog after the preview.`] });
    } else send.push(e);
  }
  return { send, skipped };
}

/** Existing same-organization catalog (active and archived) for duplicate checks. */
export async function loadExistingMaterialsForImport(actor, client = supabase) {
  const organizationId = await resolveOrganizationIdForQueries(actor, client);
  if (!organizationId) throw new Error("Your organization could not be resolved.");
  const page = 1000;
  const all = [];
  for (let from = 0; ; from += page) {
    const { data, error } = await client
      .from("materials")
      .select("material_id, material_name, sku, unit_of_measure, active")
      .eq("organization_id", organizationId)
      .order("material_id", { ascending: true })
      .range(from, from + page - 1);
    if (error) throw new Error(error.message || "Could not read the existing catalog.");
    all.push(...(data || []));
    if (!data || data.length < page) break;
  }
  return all;
}
